import { createSuccessOperationResult } from '@hc-frontend/core-utils-business-operation';

import { detectGlobalPrivacyControl } from './detect-gpc';
import { ConsentStatus } from './types';
import type { ConsentStorageRepository, GetConsentStatus } from './types';

/**
 * Factory function that creates a function for retrieving the current consent status
 *
 * This function checks for Global Privacy Control (GPC) signal when the user is in a
 * strict consent state. If GPC is enabled, it always returns REQUIRED_COOKIES status,
 * overriding any previously saved cookie preferences.
 *
 * @param deps - Dependencies
 * @param deps.storage - Storage repository for persisting consent data
 * @param deps.requiresConsent - Whether the user is in a strict consent state (e.g., California)
 * @returns A function that retrieves the current consent status
 *
 * @example
 * ```typescript
 * const getConsentStatus = getConsentStatusFactory({
 *   storage: consentStorageAdapter,
 *   requiresConsent: true
 * });
 * const result = await getConsentStatus();
 * if (result.success) {
 *   console.log(result.data); // ConsentStatus.ALL_COOKIES | REQUIRED_COOKIES | UNKNOWN | NOT_REQUIRED
 * }
 * ```
 */
export function getConsentStatusFactory(deps: {
  storage: ConsentStorageRepository;
  requiresConsent: boolean;
}): GetConsentStatus {
  return async () => {
    // Check for GPC signal in strict consent states
    // GPC always takes precedence over saved preferences
    if (deps.requiresConsent && detectGlobalPrivacyControl()) {
      return createSuccessOperationResult(ConsentStatus.REQUIRED_COOKIES);
    }

    // Fall back to stored consent status
    const status = deps.storage.getConsentStatus();
    return createSuccessOperationResult(status);
  };
}
